/**
 * In-App-Benachrichtigungen + Erinnerungs-Einstellungen (CompanyBrain Welle 2, BEN-106).
 * Hooks fuer Glocke/Badge, Liste (gelesen/ungelesen), Einstellungen, Test & Scan.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenant } from "./tenant";

export type AppNotification = {
  id: string;
  user_sub: string;
  category: string;
  severity: "info" | "warning" | "critical" | string;
  title: string;
  body: string;
  route: string;
  source: string;
  meta: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
  read: boolean;
};

export type NotificationListResponse = {
  tenant_id: string;
  user: string;
  status: string;
  count: number;
  items: AppNotification[];
  persistent: boolean;
};

export type TriggerConf = { enabled: boolean; attainment_below_pct?: number; days?: number };

export type NotificationSettings = {
  enabled: boolean;
  frequency: "immediate" | "daily" | "weekly";
  channels: { inapp: boolean; email: boolean; chat: boolean };
  email_address: string;
  triggers: Record<string, TriggerConf>;
};

export type SettingsResponse = {
  tenant_id: string;
  user: string;
  settings: NotificationSettings;
  defaults: NotificationSettings;
  persistent: boolean;
};

const KEY = {
  unread: (t?: string | null) => ["notif-unread", t],
  list: (t?: string | null, s?: string) => ["notif-list", t, s],
  settings: (t?: string | null) => ["notif-settings", t],
};

export function useUnreadCount() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: KEY.unread(effectiveTenantId),
    queryFn: () => api.get<{ count: number }>("/notifications/unread-count"),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useNotifications(status: "all" | "unread" | "read" = "all") {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: KEY.list(effectiveTenantId, status),
    queryFn: () => api.get<NotificationListResponse>(`/notifications?status=${status}&limit=50`),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 20_000,
  });
}

export function useNotificationSettings() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: KEY.settings(effectiveTenantId),
    queryFn: () => api.get<SettingsResponse>("/notification-settings"),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
  });
}

export function useMarkRead() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${encodeURIComponent(id)}/read`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });
}

export function useMarkAllRead() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/notifications/read-all", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });
}

export function useSaveSettings() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: NotificationSettings) =>
      api.put<SettingsResponse>("/notification-settings", settings),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-settings"] }),
  });
}

export function useTestNotification() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean; id: string }>("/notifications/test", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });
}

export function useRunScan() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ created: number; considered: number; candidates: number }>(
        "/notifications/scan?role=berater",
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });
}

export type EmailChannelStatus = {
  mode: string;
  ready: boolean;
  state: "aktiv" | "wartet_auf_smtp" | "geplant" | string;
  label: string;
  detail: string;
  from: string;
  default_to?: string;
  password_present: boolean;
  smtp_host_configured: boolean;
  password_file?: string;
  missing?: string[];
};

export function useEmailStatus() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["email-status", effectiveTenantId],
    queryFn: () => api.get<EmailChannelStatus>("/notifications/email-status"),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
  });
}

export type EmailTestResult = {
  ok: boolean; result: string; dry_run: boolean; recipient: string; channel: EmailChannelStatus;
};

export function useEmailTest() {
  const api = useApi();
  return useMutation({
    mutationFn: () => api.post<EmailTestResult>("/notifications/email-test?dry_run=1", {}),
  });
}

export const TRIGGER_LABELS: Record<string, string> = {
  kpi_threshold: "KPI-Schwellwert unterschritten (Ist unter Ziel)",
  ressort_verzug: "Ressort/Phase in Verzug (OE-Board rot)",
  wp_overdue: "Arbeitspakete offen/ueberfaellig",
  process_open: "Offene Prozessschritte",
  evidence_missing: "Fehlende Nachweise / offene Vorgaenge",
  due_date: "Faelligkeit/Termin erreicht",
  new_run_evidence: "Neuer Nachweis/Run vorhanden",
  inactivity: "Inaktivitaet an einem Schritt",
};
